import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Button from './Button';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { usuario, isAuthenticated, logout, isAdmin, isVendedor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { href: '/anuncios', label: 'Anúncios' },
    { href: '/planos', label: 'Planos' },
  ];

  const userMenuLinks = isAuthenticated
    ? [
        { href: '/dashboard', label: 'Dashboard', icon: '📊' },
        { href: '/meus-anuncios', label: 'Meus Anúncios', icon: '📋' },
        { href: '/favoritos', label: 'Favoritos', icon: '❤️' },
        { href: '/chat', label: 'Mensagens', icon: '💬' },
        { href: '/perfil', label: 'Meu Perfil', icon: '👤' },
        ...(isAdmin ? [{ href: '/admin', label: 'Admin', icon: '⚙️' }] : []),
      ]
    : [];

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className="text-3xl">🐴</span>
            <span className="text-xl md:text-2xl font-bold text-green-700">
              BOIL<span className="text-amber-700">X</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-green-700'
                    : 'text-gray-600 hover:text-green-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {isVendedor && (
                  <Button
                    as="link"
                    href="/criar-anuncio"
                    variant="primary"
                    size="sm"
                    leftIcon={<span>➕</span>}
                  >
                    Anunciar
                  </Button>
                )}

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-9 h-9 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-semibold">
                      {usuario?.nome?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-gray-700 font-medium max-w-[120px] truncate">
                      {usuario?.nome?.split(' ')[0]}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        isUserMenuOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20 animate-scale-in">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="font-semibold text-gray-900">{usuario?.nome}</p>
                          <p className="text-sm text-gray-500">{usuario?.email}</p>
                        </div>
                        {userMenuLinks.map((link) => (
                          <Link
                            key={link.href}
                            to={link.href}
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <span>{link.icon}</span>
                            {link.label}
                          </Link>
                        ))}
                        <div className="border-t border-gray-100 mt-2 pt-2">
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 w-full transition-colors"
                          >
                            <span>🚪</span>
                            Sair
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Button as="link" href="/login" variant="ghost">
                  Entrar
                </Button>
                <Button as="link" href="/registrar" variant="primary">
                  Cadastrar
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-green-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-100 py-4 mobile-menu-enter">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {isAuthenticated ? (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  {userMenuLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <span>{link.icon}</span>
                      {link.label}
                    </Link>
                  ))}
                  {isVendedor && (
                    <Link
                      to="/criar-anuncio"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 bg-green-700 text-white rounded-lg font-medium mt-2"
                    >
                      <span>➕</span>
                      Criar Anúncio
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg mt-2 transition-colors"
                  >
                    <span>🚪</span>
                    Sair
                  </button>
                </>
              ) : (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                  >
                    Entrar
                  </Link>
                  <Link
                    to="/registrar"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-2.5 bg-green-700 text-white rounded-lg font-medium text-center"
                  >
                    Cadastrar
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
